import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TrackerCatalyst, TrackerConference, TrackerLandscapeTrial, TrackerRegistryDoc } from '../../types';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import { assetKey, flagged, fmtLong, normHist, shortVenue } from './helpers';
import { driversOf, lineInfo, LINE_LABEL } from './readThrough';
import TrackerIcon from './TrackerIcon';

/** Timeline banner: shown above the catalyst list when the Company or Tumor type filter picks out one
 * value, so the catalysts for that scope read as a timeline instead of a flat list. Ported from tracker.js's
 * renderBanner()/lanesSVG()/landscapeLanes() (catalyst_tracker/static/tracker.js:1107-1259, plus
 * dotLabel/thPhase/arrangeCtl/tlFilterCtl). Registry dates/status come from `meta/registry` (this app's
 * docstore equivalent of the original's CT.gov cache — not a dedicated SQL table) via `registry` prop;
 * landscape trials come from Asset Radar's workbook data through `/api/landscape-trials`, not a
 * `landscape_refs` table. Scoped down from the original: no hover/pin info card (a native SVG <title>
 * tooltip instead), no keyboard nav, resize grip, or "+n" collapsed-dot chips when markers crowd together,
 * and clicking a landscape (reference) row's dot is a no-op (there's no per-catalyst detail panel in this
 * app at all yet, synthetic or otherwise — only a real catalyst id does anything via onOpenCatalyst). The
 * CSS (tracker.css's .banner/.lanes/.legend rules) is the original's, unchanged. */

const OUTCOME_COLOR: Record<string, string> = {
  positive: 'var(--pos)', approved: '#0e7490', negative: 'var(--neg)', CRL: 'var(--neg)', mixed: 'var(--delay)',
};
/** tracker.js's OC(): colors a catalyst dot by outcome; "" (occurred, never scored) and "pending" both fall
 * through to --pend, which thMarker() then swaps to magenta for an OCCURRED row (an upcoming one stays white). */
const ocColor = (outcome?: string) => OUTCOME_COLOR[outcome || ''] || 'var(--pend)';

const REG_COL: Record<string, string> = {
  RECRUITING: '#175cd3', ENROLLING_BY_INVITATION: '#175cd3', NOT_YET_RECRUITING: '#98a2b3',
  ACTIVE_NOT_RECRUITING: '#6941c6', COMPLETED: '#027a48', TERMINATED: '#b42318', WITHDRAWN: '#b42318',
  SUSPENDED: '#b54708',
};
const REG_KEY: Array<[string, string]> = [
  ['Recruiting', '#175cd3'], ['Not yet recruiting', '#98a2b3'], ['Active, not recruiting', '#6941c6'],
  ['Completed', '#027a48'], ['Suspended', '#b54708'], ['Terminated / withdrawn', '#b42318'],
];

const LINE_ORDER = ['periop', 'adjuvant', '1L', 'maint', '2L+', 'nr', 'na'];
const PH_ORDER = ['Approved', 'Phase 3', 'Phase 2', 'Phase 1/2', 'Phase 1', 'Phase not reported'];

/** tracker.js's phaseOfRows(): a study's most-advanced phase, as one of the fixed PH_ORDER labels (used
 * for the Phase filter's option list and bucketing, distinct from lanePhase()'s abbreviated chip text). */
function studyPhaseLabel(rows: TrackerCatalyst[]): string {
  const t = rows.map((r) => `${r.phase || ''} ${r.trial || ''}`).join(' ').toLowerCase();
  if (/approved|marketed/.test(t) || rows.some((r) => r.outcome === 'approved')) return 'Approved';
  if (/phase\s*(3|iii)\b|registrational|pivotal|phase 2\/3|phase ii\/iii/.test(t)) return 'Phase 3';
  if (/phase\s*(2|ii)\b|phase 1b?\/2|phase i\/ii/.test(t)) return /1b?\/2|i\/ii/.test(t) ? 'Phase 1/2' : 'Phase 2';
  if (/phase\s*(1|i)\b|first-in-human|\bfih\b|dose escalation/.test(t)) return 'Phase 1';
  return 'Phase not reported';
}

/** tracker.js's tlBms(): a row's biomarker-driver tags for the Biomarkers filter, excluding TP53/del17p
 * (too common to be a useful filter facet) and falling back to a "no biomarker selection" bucket. */
function tlBms(r: TrackerCatalyst): string[] {
  const d = [...driversOf(r)].filter((k) => k !== 'TP53/del17p');
  return d.length ? d : ['none'];
}

const LINE_SHORT: Record<string, string> = { '1L': '1L', '2L+': '2L+', adjuvant: 'Adj', periop: 'Neo', maint: 'Mnt', na: 'n/a', nr: 'NR' };
const BM_SHORT: Record<string, string> = {
  'KRAS G12D': 'G12D', 'KRAS G12C': 'G12C', 'KRAS/RAS': 'RAS', 'MTAP-deleted': 'MTAPdel', 'EGFR exon 20': 'EGFR ex20',
  'HER2-mutant': 'HER2mut', 'HER2-low': 'HER2-low', 'MSI-H/dMMR': 'MSI-H', 'TP53/del17p': 'TP53', 'BRCA/HRD': 'BRCA/HRD',
};
const bmShort = (set: Set<string>) => [...set].map((k) => BM_SHORT[k] || k).join(' · ');

const DAY_MS = 86400000;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CT.gov dates are partial: 'YYYY', 'YYYY-MM' or 'YYYY-MM-DD' (registry.py's doc comment). A bare year
 * reads as its midpoint (Jul 1) and a bare month as its midpoint (the 15th), so a partial date places a
 * dot/bar edge at a reasonable estimate instead of always snapping to the 1st. */
function parseRegDate(iso?: string): number | null {
  if (!iso) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  m = /^(\d{4})-(\d{2})$/.exec(iso);
  if (m) return Date.UTC(+m[1], +m[2] - 1, 15);
  m = /^(\d{4})$/.exec(iso);
  if (m) return Date.UTC(+m[1], 6, 1);
  return null;
}

/** regFmt(): a partial CT.gov date formatted for display — 'YYYY-MM' -> 'Mon YYYY', 'YYYY' -> 'YYYY'. */
function regFmt(d?: string): string {
  if (!d) return '?';
  const [y, m] = d.split('-');
  return m ? `${MON[+m - 1]} ${y}` : y;
}

/** tracker.js:86 `studyKey` — one timeline lane per distinct study, not per asset, so an asset in two
 * different trials (e.g. a 1L and a 2L+ study of the same drug) gets two rows. */
function studyKey(r: TrackerCatalyst): string {
  return `${assetKey(r)}|${((r.trial || '').trim() || (r.indication || '').trim()).toLowerCase()}`;
}

const NCT_RE = /NCT\d{8}/gi;

/** registry.py's ncts_in(): a catalyst's `nct` field is often left blank even when its study name or
 * guided window spells the NCT out (e.g. "Pivotal Phase 2 (NCT05147467)") — pull every NCT a row mentions,
 * not just the dedicated field, so the CT.gov registry bar can still be matched to the lane. */
function extractNcts(r: TrackerCatalyst): string[] {
  const text = `${r.nct || ''} ${r.trial || ''} ${r.window || ''}`;
  const found = text.match(NCT_RE) || [];
  return [...new Set(found.map((n) => n.toUpperCase()))];
}

/** tracker.js's landscapeLanes(): an Asset Radar landscape/calendar trial with no catalyst of its own yet,
 * placed at its ClinicalTrials.gov primary completion (fallback: start). Its own dot is drawn too — hollow
 * while upcoming, filled by outcome once past (`reference: true` marks it as not a real company-guided
 * catalyst, for the header count and the legend). */
function synthesizeLandscapeRow(t: TrackerLandscapeTrial): TrackerCatalyst | null {
  const ms = parseRegDate(t.date);
  if (!ms) return null;
  const date = new Date(ms).toISOString().slice(0, 10);
  const past = ms < Date.now();
  const st = (t.registry_status || '').toLowerCase().replace(/_/g, ' ');
  return {
    id: `ls-${t.nct}`,
    company: t.company,
    asset: t.asset,
    trial: t.trial,
    nct: t.nct,
    phase: t.phase,
    indication: t.indication,
    histology: t.histology,
    type: 'Landscape trial',
    date,
    window: `ClinicalTrials.gov primary completion ${t.date || 'not listed'}${st ? ` · ${st}` : ''}`,
    status: past ? 'past' : 'upcoming',
    outcome: past ? undefined : 'pending',
    reference: true,
    registry_status: t.registry_status,
    event: `${t.trial || t.asset || 'Study'}: Asset Radar landscape trial (timing from the registry, not company guidance)`,
  };
}

function venueLabel(venue: string, conferences: TrackerConference[]): string {
  const t = (venue || '').trim();
  if (!t || !conferences.length) return t;
  const k = t.toLowerCase();
  for (const c of conferences) {
    const candidates = [c.label, c.name, ...(c.venue_match || [])];
    if (candidates.some((m) => (m || '').trim().toLowerCase() === k)) return c.label || c.name || t;
  }
  return t;
}

/** tracker.js's swOtherLabel(), minus its `_ls`/`_pp` branch (Asset Radar landscape-trial rows this page
 * never synthesizes). */
function otherLabel(ev: string): string {
  if (/acqui|merger|tender offer|takeover/.test(ev)) return 'M&A';
  if (/licens|collaborat|partner|investment|rights|royalt|financing|offering|\bpipe\b|\bipo\b/.test(ev)) return 'Deal';
  if (/results|earnings|guidance|sales/.test(ev)) return 'Earnings';
  if (/discontinu|depriorit/.test(ev)) return 'Discontinued';
  if (/clinical hold/.test(ev)) return 'Clinical hold';
  if (/\bfda\b|\bema\b|\bnmpa\b|\bpmda\b|regulator|\bind\b|\bbla\b|\bnda\b|\bmaa\b|breakthrough|orphan|fast track/.test(ev)) return 'Regulatory';
  if (/enrol/.test(ev)) return 'Enrollment';
  if (/\bdata\b|readout/.test(ev)) return 'Data update';
  if (/\bceo\b|\bcfo\b|\bcmo\b|steps down|appoint/.test(ev)) return 'Leadership';
  if (/design|registrational|pivotal|initiat|\bstart/.test(ev)) return 'Trial plan';
  return 'Update';
}

/** tracker.js's dotLabel(): the short chip drawn above a catalyst dot. */
function dotLabel(r: TrackerCatalyst, conferences: TrackerConference[]): string {
  const t = r.type || '';
  const ev = (r.event || '').toLowerCase();
  if (/regulatory/i.test(t)) return /approv/.test(ev) || r.outcome === 'approved' ? 'Approval' : /crl/i.test(r.outcome || '') ? 'CRL' : 'FDA decision';
  if (/filing/i.test(t)) return 'Filing';
  if (/trial initiation/i.test(t)) return 'Trial start';
  if (/enrollment/i.test(t)) return 'Enrolled';
  if (/conference/i.test(t)) return venueLabel(shortVenue(r.venue || ''), conferences).replace(/\s+20(\d\d)$/, " '$1") || 'Congress';
  if (/topline/i.test(t)) return /interim/.test(ev) ? 'Interim' : /final|os\b/.test(ev) ? 'Final data' : 'Topline';
  if (/investor|r&d day/.test(ev)) return 'Investor event';
  return otherLabel(ev);
}

/** tracker.js's phaseOf()+thPhase(): the lane's most-advanced phase, as a short chip. */
function lanePhase(rows: TrackerCatalyst[]): string {
  const t = rows.map((r) => `${r.phase || ''} ${r.trial || ''}`).join(' ').toLowerCase();
  if (/approved|marketed/.test(t) || rows.some((r) => r.outcome === 'approved')) return 'Approved';
  if (/phase\s*(3|iii)\b|registrational|pivotal|phase 2\/3|phase ii\/iii/.test(t)) return /phase\s*(2\/3|ii\/iii)/.test(t) ? 'Ph2/3' : 'Ph3';
  if (/phase\s*(2|ii)\b|phase 1b?\/2|phase i\/ii/.test(t)) return /1b?\/2|i\/ii/.test(t) ? 'Ph1/2' : 'Ph2';
  if (/phase\s*(1|i)\b|first-in-human|\bfih\b|dose escalation/.test(t)) return 'Ph1';
  return '';
}

type Arrange = 'asset' | 'company' | 'line' | 'phase';

interface Lane {
  key: string;
  asset: string;
  company: string;
  trial: string;
  histology: string;
  rows: TrackerCatalyst[];
  ncts: string[];
  next: number;
}

function buildLanes(rows: TrackerCatalyst[]): { lanes: Lane[]; undated: number } {
  const byKey = new Map<string, Lane>();
  let undated = 0;
  for (const r of rows) {
    if (!r.date) { undated += 1; continue; }
    const k = studyKey(r);
    let lane = byKey.get(k);
    if (!lane) {
      lane = {
        key: k, asset: r.asset || r.company || 'Asset', company: r.company || '', trial: r.trial || '',
        histology: normHist(r.histology) || 'Tumor not reported', rows: [], ncts: [],
        next: Infinity,
      };
      byKey.set(k, lane);
    }
    lane.rows.push(r);
    for (const nct of extractNcts(r)) { if (!lane.ncts.includes(nct)) lane.ncts.push(nct); }
  }
  const nowMs = Date.now();
  const lanes = [...byKey.values()];
  for (const lane of lanes) {
    lane.rows.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const times = lane.rows.map((r) => Date.parse(r.date as string));
    const future = times.filter((t) => t >= nowMs).sort((a, b) => a - b);
    lane.next = future.length ? future[0] : Math.max(...times);
  }
  lanes.sort((a, b) => a.next - b.next || a.asset.localeCompare(b.asset));
  return { lanes, undated };
}

function groupKey(lane: Lane, arrange: Arrange): string {
  if (arrange === 'company') return lane.company || 'Sponsor not reported';
  if (arrange === 'line') {
    const r0 = lane.rows[0];
    return `${lineInfo(r0).code}|${bmShort(driversOf(r0))}`;
  }
  if (arrange === 'phase') return studyPhaseLabel(lane.rows);
  return '';
}
function groupLabel(key: string, arrange: Arrange): string {
  if (arrange !== 'line') return key;
  const [code, bm] = key.split('|');
  return `${LINE_LABEL[code] || code} · ${bm || 'no biomarker selection'}`;
}

type Row = { kind: 'header'; label: string } | { kind: 'lane'; lane: Lane; sameAsPrevAsset: boolean };

function buildRows(lanesAll: Lane[], arrange: Arrange, maxLanes: number): { rows: Row[]; shown: number; extra: number } {
  if (arrange === 'asset') {
    const shown = lanesAll.slice(0, maxLanes);
    let prevAsset: string | null = null;
    const rows: Row[] = shown.map((lane) => {
      const same = prevAsset === lane.asset;
      prevAsset = lane.asset;
      return { kind: 'lane', lane, sameAsPrevAsset: same };
    });
    return { rows, shown: shown.length, extra: Math.max(0, lanesAll.length - shown.length) };
  }
  const groups = new Map<string, Lane[]>();
  for (const lane of lanesAll) {
    const k = groupKey(lane, arrange);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(lane);
  }
  const groupOrder = [...groups.entries()]
    .map(([k, lanes]) => ({ k, next: Math.min(...lanes.map((l) => l.next)) }))
    .sort((a, b) => a.next - b.next);
  const rows: Row[] = [];
  let shown = 0;
  let prevAsset: string | null = null;
  outer:
  for (const { k } of groupOrder) {
    rows.push({ kind: 'header', label: groupLabel(k, arrange) });
    prevAsset = null;
    for (const lane of groups.get(k)!) {
      if (shown >= maxLanes) break outer;
      const same = prevAsset === lane.asset;
      prevAsset = lane.asset;
      rows.push({ kind: 'lane', lane, sameAsPrevAsset: same });
      shown += 1;
    }
  }
  return { rows, shown, extra: Math.max(0, lanesAll.length - shown) };
}

interface Props {
  /** Rows already matching the Company/Tumor type (and other) filters, with no `when`/`within` restriction
   * applied — the banner plots a scope's whole history, not just its upcoming slice. */
  rows: TrackerCatalyst[];
  registry: TrackerRegistryDoc;
  conferences: TrackerConference[];
  title: string;
  companyActive: boolean;
  histologyActive: boolean;
  onClear: () => void;
  onOpenCatalyst: (id: string) => void;
}

const MAX_LANES = 60;
const L = 230, R = 20, ROW_H = 46, HEADER_H = 22, AXIS_H = 28, TOP = 8;

const TimelineBanner: React.FC<Props> = ({
  rows, registry, conferences, title, companyActive, histologyActive, onClear, onOpenCatalyst,
}) => {
  const [arrange, setArrange] = useState<Arrange>('asset');
  const scrollRef = useRef<HTMLDivElement>(null);
  const tumorOnly = histologyActive && !companyActive;
  // tracker.js's arrangeCtl(): always the same options, regardless of scope (grouping by Company in a
  // single-company view is just a no-op single group, not worth hiding).
  const arrangeOptions: { v: Arrange; label: string }[] = [
    { v: 'asset', label: 'Asset' }, { v: 'company', label: 'Company' },
    { v: 'line', label: 'Treatment line / biomarker' }, { v: 'phase', label: 'Phase' },
  ];
  const effArrange = arrange;

  // tracker.js's `S.lsTrials` toggle (default on): in a pure tumor-type view, overlays Asset Radar
  // landscape/calendar trials for this tumor type that have no catalyst of their own yet.
  const [landscapeOn, setLandscapeOn] = useState(true);
  const [landscapeRows, setLandscapeRows] = useState<TrackerLandscapeTrial[]>([]);
  // tracker.js's tlFilterCtl(): the "Filter" row's three multiselects (line of treatment, biomarker
  // population, development phase), each independent of the other and reset whenever the scope changes.
  const [selLines, setSelLines] = useState<string[]>([]);
  const [selBms, setSelBms] = useState<string[]>([]);
  const [selPhases, setSelPhases] = useState<string[]>([]);
  useEffect(() => { setLandscapeRows([]); setSelLines([]); setSelBms([]); setSelPhases([]); }, [title]);
  useEffect(() => {
    if (!landscapeOn || !tumorOnly) return;
    let cancelled = false;
    catalystTrackerApi.getLandscapeTrials(title).then((rs) => { if (!cancelled) setLandscapeRows(rs); })
      .catch(() => { if (!cancelled) setLandscapeRows([]); });
    return () => { cancelled = true; };
  }, [landscapeOn, tumorOnly, title]);

  const effectiveRows = useMemo(() => {
    if (!landscapeOn || !tumorOnly) return rows;
    // landscapeLanes()'s `have`: an NCT already on the calendar (in any row's nct/trial/window text) never
    // gets a duplicate synthetic landscape row.
    const haveNcts = new Set(rows.flatMap(extractNcts));
    const seen = new Set<string>();
    const extraRows: TrackerCatalyst[] = [];
    for (const t of landscapeRows) {
      const nct = (t.nct || '').toUpperCase();
      if (!nct || haveNcts.has(nct) || seen.has(nct)) continue;
      const synthesized = synthesizeLandscapeRow(t);
      if (!synthesized) continue;
      seen.add(nct);
      extraRows.push(synthesized);
    }
    return [...rows, ...extraRows];
  }, [rows, landscapeRows, landscapeOn, tumorOnly]);

  // tracker.js's studyPhases(): bucket every dated row's study by its most-advanced phase, so the Phase
  // filter (and tlOK()) judge a study by its full row set, not just whichever rows survive other filters.
  const studyPhaseByKey = useMemo(() => {
    const by = new Map<string, TrackerCatalyst[]>();
    effectiveRows.forEach((r) => { if (!r.date) return; const k = studyKey(r); if (!by.has(k)) by.set(k, []); by.get(k)!.push(r); });
    return new Map([...by].map(([k, rs]) => [k, studyPhaseLabel(rs)]));
  }, [effectiveRows]);

  const lineOptions = useMemo<[string, string][]>(() => {
    const present = new Set(effectiveRows.filter((r) => r.date).map((r) => lineInfo(r).code));
    return LINE_ORDER.filter((c) => present.has(c) && LINE_LABEL[c]).map((c) => [c, LINE_LABEL[c]]);
  }, [effectiveRows]);
  const bmOptions = useMemo<[string, string][]>(() => {
    const present = new Set(effectiveRows.filter((r) => r.date).flatMap(tlBms));
    return [...present].sort((a, b) => (a === 'none' ? 1 : b === 'none' ? -1 : a.localeCompare(b)))
      .map((b) => [b, b === 'none' ? 'No biomarker selection' : b]);
  }, [effectiveRows]);
  const phaseOptions = useMemo<[string, string][]>(() => {
    const present = new Set(studyPhaseByKey.values());
    return PH_ORDER.filter((p) => present.has(p)).map((p) => [p, p]);
  }, [studyPhaseByKey]);

  // tracker.js's tlOK(): a row passes when it matches every active multiselect (an empty selection means
  // "don't filter on this facet").
  const tlOK = (r: TrackerCatalyst) => {
    if (selLines.length && !selLines.includes(lineInfo(r).code)) return false;
    if (selBms.length && !tlBms(r).some((b) => selBms.includes(b))) return false;
    if (selPhases.length && !selPhases.includes(studyPhaseByKey.get(studyKey(r)) || studyPhaseLabel([r]))) return false;
    return true;
  };
  const filteredRows = useMemo(() => effectiveRows.filter(tlOK),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tlOK closes over selLines/selBms/selPhases/studyPhaseByKey, listed below
    [effectiveRows, selLines, selBms, selPhases, studyPhaseByKey]);

  const { lanes: lanesAll, undated } = useMemo(() => buildLanes(filteredRows), [filteredRows]);
  const { rows: laidOut, shown, extra } = useMemo(
    () => buildRows(lanesAll, effArrange, MAX_LANES),
    [lanesAll, effArrange],
  );
  const laneRows = laidOut.filter((r): r is Row & { kind: 'lane' } => r.kind === 'lane');
  const assetsCount = useMemo(() => new Set(effectiveRows.map(assetKey)).size, [effectiveRows]);
  const studiesCount = useMemo(() => new Set(effectiveRows.map(studyKey)).size, [effectiveRows]);
  const landscapeIncluded = useMemo(() => effectiveRows.filter((r) => r.reference).length, [effectiveRows]);
  const hasLandscape = landscapeIncluded > 0;

  const { t0, t1, chartW } = useMemo(() => {
    // Event dates (the dots) and registry dates (the bars) feed the axis range differently: the end of the
    // axis extends to cover a lane's primary completion (capped so one far-future trial can't blow out the
    // whole chart), while the start only reaches back past 2026 when a landscape row needs the room.
    const eventDates: number[] = [];
    const regPDates: number[] = [];
    laneRows.forEach(({ lane }) => {
      lane.rows.forEach((r) => eventDates.push(Date.parse(r.date as string)));
      lane.ncts.forEach((nct) => {
        const reg = registry[nct];
        const s = parseRegDate(reg?.s), p = parseRegDate(reg?.p);
        if (s) eventDates.push(s);
        if (p) regPDates.push(p);
      });
    });
    const nowMs = Date.now();
    if (!eventDates.length) eventDates.push(nowMs);
    let start = Math.min(...eventDates, Date.parse('2026-01-01'));
    if (hasLandscape) start = Math.max(start, Date.parse('2020-01-01'));
    start = Date.UTC(new Date(start).getUTCFullYear(), new Date(start).getUTCMonth(), 1);
    const latestReg = regPDates.length ? Math.max(...regPDates) : -Infinity;
    const end = Math.max(...eventDates, Date.parse('2027-12-31'), Math.min(latestReg, Date.parse('2030-12-31')));
    const spanYears = Math.max(0.5, (end - start) / DAY_MS / 365);
    const cw = Math.max(560, Math.round(spanYears * 180));
    return { t0: start, t1: end, chartW: cw };
  }, [laneRows, registry, hasLandscape]);

  if (!shown) {
    return (
      <div className="banner">
        <div className="banner-head">
          <div>
            <h2>{title}</h2>
            <p>No dated catalysts match these filters yet{undated ? ` (${undated} without a known date)` : ''}.</p>
          </div>
          <div className="bctl">
            <button type="button" className="ib tip-r" title="Back to all catalysts" aria-label="Back to all catalysts" onClick={onClear}>&times;</button>
          </div>
        </div>
      </div>
    );
  }

  const W = L + chartW + R;
  // y-position of every row (header or lane), computed once so headers and lanes share one coordinate space.
  const ys: number[] = [];
  let cursor = TOP;
  for (const r of laidOut) {
    if (r.kind === 'header') { ys.push(cursor); cursor += HEADER_H; } else { ys.push(cursor + ROW_H / 2); cursor += ROW_H; }
  }
  const H = cursor + 10;

  const x = (ms: number) => L + ((ms - t0) / (t1 - t0)) * chartW;
  const clampX = (ms: number) => Math.max(L, Math.min(W - R, x(ms)));
  const todayMs = Date.now();
  const todayX = clampX(todayMs);

  // tracker.js's thAnchorLeft(): bring Today near the left edge of the visible plot on open, rather than
  // leaving the viewer looking at the start of the axis with nothing interesting scrolled into view. When
  // landscape rows stretch the axis back to 2020, anchor further in (today ~35% from the left of the
  // viewport) so those older marks are still reachable by scrolling left instead of sitting off-screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-anchor when the scope (title) changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const offset = hasLandscape ? el.clientWidth * 0.35 : 60;
    el.scrollLeft = Math.max(0, todayX - L - offset);
  }, [title]);

  const ticks: { x: number; label: string; year?: string }[] = [];
  const qStart = new Date(t0);
  qStart.setUTCDate(1);
  qStart.setUTCMonth(Math.floor(qStart.getUTCMonth() / 3) * 3);
  for (let q = qStart.getTime(); q <= t1;) {
    const d = new Date(q);
    const qn = Math.floor(d.getUTCMonth() / 3) + 1;
    ticks.push({ x: clampX(q), label: `Q${qn}`, year: qn === 1 ? String(d.getUTCFullYear()) : undefined });
    d.setUTCMonth(d.getUTCMonth() + 3);
    q = d.getTime();
  }

  return (
    <div className="banner">
      <div className="banner-head">
        <div>
          <h2>{title}</h2>
          <p>
            {assetsCount} assets, {studiesCount} studies, past and expected
            {landscapeIncluded ? ` · includes ${landscapeIncluded} Asset Radar landscape trial${landscapeIncluded === 1 ? '' : 's'} not yet on the calendar` : ''}
          </p>
        </div>
        <div className="bctl">
          {tumorOnly && (
            <button
              type="button"
              className="ib"
              aria-pressed={landscapeOn}
              onClick={() => setLandscapeOn((v) => !v)}
              title={`${landscapeOn ? 'Showing' : 'Hiding'} Asset Radar landscape trials that have no catalyst row yet (placed at their ClinicalTrials.gov primary completion). Click to ${landscapeOn ? 'hide' : 'show'} them.`}
              style={{ width: 'auto', padding: '0 10px', fontSize: '12.5px' }}
            >
              <TrackerIcon name="layers" /> Landscape trials
            </button>
          )}
          <div className="bfil">
            <span className="arrl">Filter</span>
            <MultiSelect noun="lines" options={lineOptions} selected={selLines} onChange={setSelLines} />
            <MultiSelect noun="biomarkers" options={bmOptions} selected={selBms} onChange={setSelBms} />
            <MultiSelect noun="phases" options={phaseOptions} selected={selPhases} onChange={setSelPhases} />
          </div>
          <div className="seg sm" role="group" aria-label="Arrange rows by">
            <span className="arrl">Arrange by</span>
            {arrangeOptions.map((o) => (
              <button key={o.v} type="button" aria-pressed={effArrange === o.v} onClick={() => setArrange(o.v)}>{o.label}</button>
            ))}
          </div>
          <button type="button" className="ib tip-r" title="Back to all catalysts" aria-label="Back to all catalysts" onClick={onClear}>&times;</button>
        </div>
      </div>

      <div className="legend">
        <b>Catalysts</b>
        <span><i style={{ background: 'var(--pos)' }} />Positive</span>
        <span><i style={{ background: '#0e7490' }} />Approved</span>
        <span><i style={{ background: 'var(--neg)' }} />Negative / CRL</span>
        <span><i style={{ background: 'var(--delay)' }} />Mixed</span>
        <span><i style={{ background: '#c11574' }} />Occurred, not scored</span>
        <span><i style={{ border: '2px solid #101828', background: '#fff' }} />Expected</span>
        <span><i style={{ border: '2.5px solid #b54708', background: '#fff' }} />Delayed or overdue</span>
        <b>Trials</b>
        {REG_KEY.map(([label, col]) => (
          <span key={label}><i style={{ width: 18, height: 8, borderRadius: 4, background: col, opacity: 0.75 }} />{label}</span>
        ))}
      </div>

      <div className="lanes-scroll" ref={scrollRef}>
        <div className="lanes-axis">
          <div className="axsp" style={{ width: L }} />
          <svg className="axis" width={chartW} height={AXIS_H} viewBox={`${L} 0 ${chartW} ${AXIS_H}`} aria-hidden="true">
            {ticks.map((t, i) => (
              <React.Fragment key={i}>
                <line x1={t.x} x2={t.x} y1={t.year ? 0 : AXIS_H - 10} y2={AXIS_H} stroke={t.year ? '#c9ccd6' : 'var(--line)'} />
                <text x={t.x + 3} y={AXIS_H - 8} style={{ fontSize: 10.5, fontWeight: 600, fill: 'var(--tl-muted)' }}>{t.label}</text>
                {t.year && <text x={t.x + 3} y={13} style={{ fontSize: 12, fontWeight: 700, fill: 'var(--ink)' }}>{t.year}</text>}
              </React.Fragment>
            ))}
            <line x1={todayX} x2={todayX} y1={16} y2={AXIS_H} stroke="var(--tl-accent)" strokeWidth={1.5} />
            <text x={todayX} y={13} textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: 'var(--tl-accent)' }}>Today</text>
          </svg>
        </div>
        <div className="lanes-row">
          <svg className="lanes lbls" data-l={L} width={L} height={H} viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Studies">
            <defs><clipPath id="tlLblClip"><rect x={0} y={0} width={L - 12} height={H} /></clipPath></defs>
            {laidOut.map((r, i) => {
              const y = ys[i];
              if (r.kind === 'header') {
                return (
                  <g key={`h${i}`}>
                    <rect x={0} y={y + 2} width={L} height={HEADER_H - 4} fill="var(--sunk)" />
                    <text x={6} y={y + HEADER_H / 2 + 4} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', fill: 'var(--tl-muted)' }}>{r.label}</text>
                  </g>
                );
              }
              const { lane, sameAsPrevAsset } = r;
              const r0 = lane.rows[0];
              const line = lineInfo(r0).code;
              const lineTxt = LINE_SHORT[line] || line;
              const phase = lanePhase(lane.rows);
              const bm = bmShort(driversOf(r0));
              const highImpact = lane.rows.some((row) => row.impact === 'High');
              return (
                <g key={lane.key} clipPath="url(#tlLblClip)">
                  {!sameAsPrevAsset && <text x={4} y={y - 14} className="lab" style={{ fontSize: 13, fontWeight: 700 }}>{lane.asset}</text>}
                  <text x={4} y={y + 1} style={{ fontSize: 10.5, fontWeight: 700, fill: 'var(--accent)' }}>
                    {lineTxt}{phase ? ` · ${phase}` : ''}{bm ? ` · ${bm}` : ''}{highImpact ? ' · High impact' : ''}
                  </text>
                  <text x={4} y={y + 15} style={{ fontSize: 11, fill: 'var(--muted)' }}>
                    {[lane.trial, lane.company].filter(Boolean).join(' · ') || 'Sponsor not reported'}
                  </text>
                </g>
              );
            })}
          </svg>
          <svg className="lanes chart" width={chartW} height={H} viewBox={`${L} 0 ${chartW} ${H}`} role="img" aria-label="Timeline of catalysts by study">
            <rect x={L} y={0} width={Math.max(0, todayX - L)} height={H} fill="var(--tl-past)" style={{ pointerEvents: 'none' }} />
            {laidOut.map((r, i) => {
              const y = ys[i];
              if (r.kind === 'header') {
                return <rect key={`h${i}`} x={L} y={y + 2} width={W - L} height={HEADER_H - 4} fill="var(--sunk)" />;
              }
              const { lane } = r;
              return (
                <g key={lane.key}>
                  <line className="lane" x1={L} x2={W - R} y1={y} y2={y} />
                  {lane.ncts.map((nct) => {
                    const reg = registry[nct];
                    const s = parseRegDate(reg?.s);
                    if (!s) return null;
                    const p = parseRegDate(reg?.p);
                    // A trial with a known start but no (or an inverted) primary completion is still
                    // ongoing — run its bar to the right edge of the axis rather than drop it.
                    const hasCompletion = !!p && p > s;
                    const xa = clampX(s), xb = hasCompletion ? clampX(p as number) : W - R;
                    const col = REG_COL[reg?.st || ''] || '#98a2b3';
                    const startInAxis = s >= t0 && s <= t1;
                    const completionInAxis = hasCompletion && (p as number) >= t0 && (p as number) <= t1;
                    return (
                      <g key={nct} className="regbar">
                        <title>
                          {`ClinicalTrials.gov ${nct}\nStudy start: ${regFmt(reg?.s)}\nPrimary completion: ${regFmt(reg?.p)}\nStatus: ${(reg?.st || 'not stated').toLowerCase().replace(/_/g, ' ')}`}
                        </title>
                        <rect x={xa} y={y - 5} width={Math.max(2, xb - xa)} height={10} rx={5} fill={col} opacity={0.5} />
                        {startInAxis && <rect x={xa - 0.75} y={y - 7} width={1.5} height={14} fill={col} />}
                        {completionInAxis && (
                          <path d={`M${xb} ${y - 5} L${xb + 5} ${y} L${xb} ${y + 5} L${xb - 5} ${y} Z`} fill={col} stroke="#fff" strokeWidth={1.5} />
                        )}
                      </g>
                    );
                  })}
                  {(() => { let lastLabelX = -Infinity; return lane.rows.map((row) => {
                    const cx = clampX(Date.parse(row.date as string));
                    const up = row.status === 'upcoming';
                    const fl = flagged(row);
                    const occurredUnscored = !up && !row.outcome;
                    const fill = up ? '#ffffff' : occurredUnscored ? '#c11574' : ocColor(row.outcome);
                    const stroke = fl ? '#b54708' : up ? 'var(--tl-ink)' : '#ffffff';
                    // A landscape row has no company-guided event to caption — just its hollow/filled dot.
                    const label = row.reference ? '' : dotLabel(row, conferences);
                    // tracker.js's `dl = lab && cx - lastLx > 70`: drop a label that would collide with the
                    // previous one in this lane rather than render illegible overlapping text (no "+n" chip
                    // or hover expansion here, so a close cluster just shows its dots, unlabeled).
                    const showLabel = !!label && cx - lastLabelX > 60;
                    if (showLabel) lastLabelX = cx;
                    return (
                      <g key={row.id}>
                        {fl && <circle cx={cx} cy={y} r={13} fill="#fffaeb" style={{ pointerEvents: 'none' }} />}
                        {showLabel && (
                          <text x={cx} y={y - 13} textAnchor="middle" style={{ fontSize: 10.5, fontWeight: 600, fill: 'var(--tl-ink)', pointerEvents: 'none' }}>{label}</text>
                        )}
                        <circle
                          cx={cx}
                          cy={y}
                          r={7}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={fl ? 2.5 : up ? 2 : 1.5}
                          style={{ cursor: 'pointer' }}
                          onClick={() => onOpenCatalyst(row.id)}
                        >
                          <title>{`${row.event || row.type || 'Catalyst'}\n${fmtLong(row.date)}${row.outcome ? ` · ${row.outcome}` : ''}${fl ? ' · delayed/overdue' : ''}`}</title>
                        </circle>
                      </g>
                    );
                  }); })()}
                </g>
              );
            })}
            <line className="today" x1={todayX} x2={todayX} y1={0} y2={H} />
          </svg>
        </div>
      </div>
      {extra > 0 && (
        <p className="undated">
          Showing the nearest {shown} of {shown + extra} studies for this scope; {extra} more {extra === 1 ? 'is' : 'are'} not shown.
        </p>
      )}
    </div>
  );
};

/** tracker.js's msCtl(): a checkbox-popover multiselect chip, used for the banner's Lines/Biomarkers/Phases
 * filters. An empty `selected` means "don't filter on this facet" (shown as "All {noun}"). */
const MultiSelect: React.FC<{
  noun: string; options: [string, string][]; selected: string[]; onChange: (next: string[]) => void;
}> = ({ noun, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const sel = selected.filter((v) => options.some(([ov]) => ov === v));
  const label = !sel.length ? `All ${noun}` : sel.length === 1 ? (options.find(([ov]) => ov === sel[0])?.[1] || sel[0]) : `${sel.length} ${noun}`;
  return (
    <div className={`ms${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="msb" aria-haspopup="true" aria-expanded={open} aria-label={`Filter ${noun}`} onClick={() => setOpen((v) => !v)}>
        <span className="msl">{label}</span>
        {sel.length > 0 && <span className="msn">{sel.length}</span>}
        <TrackerIcon name="chev" />
      </button>
      {open && (
        <div className="mspop" role="group" aria-label={noun}>
          {options.map(([v, t]) => (
            <label key={v}>
              <input type="checkbox" checked={sel.includes(v)} onChange={() => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v])} /> {t}
            </label>
          ))}
          <div className="msft">
            <button type="button" onClick={() => onChange([])}>Clear</button>
            <button type="button" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineBanner;
